import { useState, useCallback } from 'react';

import {
  FetchLifestylesSetsCacheKey,
  FetchLifestylesSetByUserIdScopeType
} from '../../../../../lifestylesSets/lifestylesSetsTypes';

import {
  FETCH_LIFESTYLES_SET_BY_USER_ID_QUERY,
  FetchLifestylesSetByUserIdQueryResponse
} from '../../../../../lifestylesSets/queries/fetchLifestylesSetByUserId.query';

import { useLifestylesSetByUserId } from '../../../../../lifestylesSets/hooks/useLifestylesSetByUserId';
import { useCurrentUser } from '../../../../../../auth/hooks/useAuth/useAuth';

import { LifestylesListOnLifestylesSelect } from '../../../../components/lists/LifestylesList';

interface LifestylesIndexPageSelectedLifestylesOptions {
  lifestylesSetScope?: FetchLifestylesSetByUserIdScopeType;
  lifestylesSetCacheKey: FetchLifestylesSetsCacheKey;
  onLifestylesSelect?: LifestylesListOnLifestylesSelect;
}

function useLifestylesIndexPageSelectedLifestyles({
  lifestylesSetScope,
  lifestylesSetCacheKey,
  onLifestylesSelect
}: LifestylesIndexPageSelectedLifestylesOptions) {
  const currentUser = useCurrentUser();

  const {
    lifestylesSet,
    lifestylesSetError,
    lifestylesSetFetched,
    lifestylesSetIsPlaceholderData,
    updateLifestylesSetCache
  } = useLifestylesSetByUserId<FetchLifestylesSetByUserIdQueryResponse>({
    cacheKey: lifestylesSetCacheKey,
    query: FETCH_LIFESTYLES_SET_BY_USER_ID_QUERY,
    userId: currentUser.get('id'),
    scope: lifestylesSetScope,
    options: {
      onSuccess: (data) =>
        data?.lifestylesSetByUserId?.selectedLifestyles
          ? onLifestylesSelect?.(
              data?.lifestylesSetByUserId?.selectedLifestyles
            )
          : undefined
    }
  });

  const [isSelectedLifestylesSidebarOpen, setIsSelectedLifestylesSidebarOpen] =
    useState(false);

  const handleSelectedLifestylesSidebarOpen = useCallback<() => void>(() => {
    setIsSelectedLifestylesSidebarOpen(true);
  }, [setIsSelectedLifestylesSidebarOpen]);

  const handleSelectedLifestylesSidebarClose = useCallback<() => void>(() => {
    setIsSelectedLifestylesSidebarOpen(false);
  }, [setIsSelectedLifestylesSidebarOpen]);

  return {
    lifestylesSet,
    lifestylesSetError,
    lifestylesSetFetched,
    isOptimisticUpdateData: lifestylesSet?.optimisticUpdate,
    lifestylesSetIsPlaceholderData,
    updateLifestylesSetCache,
    isSelectedLifestylesSidebarOpen,
    handleSelectedLifestylesSidebarOpen,
    handleSelectedLifestylesSidebarClose
  };
}

export default useLifestylesIndexPageSelectedLifestyles;
